/* eslint-disable local-rules/no-race-conditions --
 * Rule matches identifier names only and flags 82 false positives in this
 * file (language keywords like `if`/`return`/`catch`, built-ins like `String`/
 * `slice`, fire-and-forget logging like `captureEvent`/`captureMessage`, and
 * React state setters like `setOnAuthError`). None are actual race conditions.
 * Re-enable after NocoDB task to fix the rule's heuristic lands.
 */
/**
 * AuthContext — Better Auth session management
 *
 * Uses imperative authClient.getSession() instead of the nanostores-backed
 * useSession() hook. The reactive hook returns { data: null } on Expo web
 * due to BA's $fetch credential handling / nanostores hydration race.
 * Better Auth is the sole auth provider.
 */
import React, { createContext, useCallback, useEffect, useContext, useRef, useMemo, useState, type ReactNode } from "react";
import { Platform } from "react-native";
import * as Sentry from "@sentry/react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
  getAnalysisHistory,
  queryKeys,
  decodeJwtPayload,
  setOnAuthError,
  authenticatedFetch,
  configureAuthToken,
  setAuthStatus,
  getAuthStatus,
  isAuthRequiredError,
  isCancelledError,
  isTransientFetchError,
  type AuthError,
  QUERY_CACHE_KEY,
} from "@dramadetector/api-client";
import { authClient } from "../lib/better-auth-client";
import { toAuthError, fetchBetterAuthJwt } from "./auth-utils";
import { identifyUser, clearUser as clearAnalyticsUser, captureEvent, getPostHog } from "../lib/analytics";
import { scrubString } from "../lib/analytics/scrubbers";
import { getDeviceId } from "../lib/device";
import { getEdgeFunctionUrl } from "../lib/api-url";
import * as Linking from 'expo-linking';
import { useQueryClient } from '@tanstack/react-query';
import { useAnalysisStore } from '../store/analysis';
import { useUserStore } from '../store/user';
import { useImperativeSession } from '../hooks/useImperativeSession';
import { usePathname } from 'expo-router';
import {
  EXPLICIT_SIGNOUT_KEY,
  EXPLICIT_SIGNOUT_TTL_MS,
  shouldBootstrapAnon,
} from '../lib/auth-bootstrap';

/**
 * BA-native user object exposed by AuthContext.
 * No Supabase types — maps directly from Better Auth session data.
 */
export interface AuthUser {
  id: string;
  email: string;
  name?: string;
  image?: string | null;
  emailVerified: boolean;
  createdAt: string; // ISO 8601
  /**
   * True when the BA user record was created via the anonymous plugin.
   * Phase 2 (NocoDB #3844 / EPIC #3840): consumers use this to gate queries
   * that only make sense for real authenticated users (onboarding status,
   * last-active updates, preferences) and to avoid the wasted 401 traffic
   * that was triggering the signOut cascade.
   */
  isAnonymous?: boolean;
}

interface AuthContextType {
  /** JWT access token for API calls, or null when not authenticated / JWT pending. */
  accessToken: string | null;
  /** Authenticated user, or null when signed out. */
  user: AuthUser | null;
  loading: boolean;
  /**
   * True when the caller is semantically anonymous — either NO session at all,
   * OR a Better Auth anonymous session (user.isAnonymous=true from the BA
   * anonymous plugin). Phase 2 (NocoDB #3869 / EPIC #3840) close-out: consumers
   * MUST use this derived boolean instead of `!accessToken` because anon BA
   * users HAVE an accessToken (the JWT) but are not real authenticated users.
   * Using `!accessToken` misclassifies them and hides anon-only UI (quota
   * counter, tier badges, etc.) — the bug class that cost us 4 whack-a-mole PRs.
   */
  isAnonymous: boolean;
  /**
   * Inverse of `isAnonymous`: true only when the caller is a real authenticated
   * user with a valid session (not anonymous, not logged-out). Use for:
   *   - gating React Query `enabled` flags on authed-only endpoints (onboarding,
   *     subscription, preferences, etc.) — anon BA users should NOT fire them
   *   - error-fallback branching in entitlements/tier hooks — anon BA users
   *     must receive anon-tier defaults, not free-tier defaults
   *   - analytics tier classification — anon BA users report as 'anonymous',
   *     not 'free'
   */
  isRealUser: boolean;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: (options?: { explicit?: boolean }) => Promise<{ error: AuthError | null }>;
  signInWithGoogle: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * FF_BA_ANONYMOUS_BOOTSTRAP — feature flag gate for Phase 2 (#3843 / EPIC #3840).
 *
 * When ON (EXPO_PUBLIC_FF_BA_ANONYMOUS_BOOTSTRAP=true), AuthProvider calls
 * authClient.signIn.anonymous() on first mount if no BA session exists. This
 * ensures every request carries a real BA JWT (anon or authed), eliminating the
 * split-brain bug class at the source. The anon BA userId replaces the device-id
 * as the anonymous identity primitive.
 *
 * When OFF (default), AuthContext behaves as pre-Phase-2: device-id flow only.
 * Rollout plan: merge with flag OFF → flip ON in staging (24h soak) → flip ON
 * in prod (24h Sentry monitoring) → Phase 3 can start.
 *
 * Only the literal string 'true' enables this flag.
 */
const FF_BA_ANONYMOUS_BOOTSTRAP =
  process.env.EXPO_PUBLIC_FF_BA_ANONYMOUS_BOOTSTRAP === 'true';

export function AuthProvider({ children }: { children: ReactNode }) {
  // Phase 2 fix: imperative session fetch extracted to useImperativeSession hook.
  // See hook file for rationale (nanostores hydration race on Expo web).
  const { baSession, sessionLoading, refreshSession, clearSession } = useImperativeSession();

  const queryClient = useQueryClient();
  const clearHistory = useAnalysisStore(state => state.clearHistory);
  const resetPreferences = useUserStore(state => state.resetPreferences);
  const prevUserIdRef = useRef<string | null>(null);
  const prevUserWasAnonRef = useRef<boolean>(false);
  // Tracks which user we've already prefetched history for. Prevents re-running
  // the prefetch on JWT rotation, but allows a fresh prefetch when a different
  // user signs in. Reset to null on sign-out.
  const prefetchDoneForUserRef = useRef<string | null>(null);
  const authErrorSignOutLockRef = useRef(false);
  const authErrorCooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Prevents anonymous auto-signin from firing more than once per mount.
  // useRef (not useState) so the guard never triggers a re-render.
  const anonSignInFiredRef = useRef(false);

  // Tracks an explicit user-initiated sign-out. When true, the anon bootstrap
  // must NOT re-fire — the user intentionally left the authenticated state.
  // Synchronous ref (not state) so it blocks the bootstrap effect immediately
  // on the same render cycle without waiting for a state update tick.
  const explicitSignedOutRef = useRef(false);

  // True once the AsyncStorage hydration of explicitSignedOutRef is complete.
  // Bootstrap effect MUST NOT fire until this is true — the hydration is async
  // and racing it would create an anon session before reading a persisted intent.
  const [intentHydrated, setIntentHydrated] = useState(false);

  const pathname = usePathname();

  // JWT obtained from Better Auth's /api/auth/token endpoint.
  // Starts null; populated asynchronously after session is available.
  const [jwt, setJwt] = useState<string | null>(null);
  // True when JWT mint failed after all retries (auth_broken state).
  // Excluded from the loading condition so components can render and trigger
  // authenticatedFetch → onAuthError → signOut self-heal (NocoDB #3830).
  const [authBroken, setAuthBroken] = useState(false);
  const jwtFetchedForTokenRef = useRef<string | null>(null);
  // Queued deviceId for anonymous data claim (#2896). Set by the user-id effect on
  // first login; consumed by the configureDbClient effect once jwt is available.
  const claimPendingDeviceIdRef = useRef<string | null>(null);

  // Hydrate the explicit sign-out intent from AsyncStorage on mount.
  // Must complete before the bootstrap effect can fire (intentHydrated gate).
  // A stored entry older than 30 days is treated as stale and removed.
  useEffect(() => {
    AsyncStorage.getItem(EXPLICIT_SIGNOUT_KEY).then((raw) => {
      if (raw) {
        try {
          const { at } = JSON.parse(raw) as { at: number };
          if (Date.now() - at < EXPLICIT_SIGNOUT_TTL_MS) {
            explicitSignedOutRef.current = true;
          } else {
            // Stale — remove and do not block bootstrap
            AsyncStorage.removeItem(EXPLICIT_SIGNOUT_KEY).catch(() => {});
          }
        } catch {
          // Corrupt entry — ignore
          AsyncStorage.removeItem(EXPLICIT_SIGNOUT_KEY).catch(() => {});
        }
      }
      setIntentHydrated(true);
    }).catch(() => {
      // Storage read failure must not block bootstrap indefinitely
      setIntentHydrated(true);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 2 (NocoDB #3843 / EPIC #3840): anonymous auto-signin on first mount.
  //
  // When FF_BA_ANONYMOUS_BOOTSTRAP is ON and no BA session exists after the
  // initial session fetch resolves, call signIn.anonymous() once. This mints a
  // real BA anonymous session so every subsequent request carries a JWT Bearer
  // instead of relying on the device-id header. The anon BA userId becomes the
  // stable anonymous identity (device-id path is preserved for Phase-2 OFF and
  // as a fallback until Phase 3 drops device_id columns).
  //
  // Guard: shouldBootstrapAnon() centralises all conditions. explicitSignedOutRef
  // blocks re-bootstrap after explicit sign-out (NocoDB #3906 / AUTH-UX-A fix).
  useEffect(() => {
    if (!shouldBootstrapAnon({
      ffEnabled: FF_BA_ANONYMOUS_BOOTSTRAP,
      intentHydrated,
      sessionLoading,
      hasBaSession: !!baSession,
      anonFired: anonSignInFiredRef.current,
      explicitSignedOut: explicitSignedOutRef.current,
      pathname,
    })) return;
    anonSignInFiredRef.current = true;

    Sentry.addBreadcrumb({
      category: 'auth.anon',
      message: 'Anonymous auto-signin triggered (FF_BA_ANONYMOUS_BOOTSTRAP)',
      level: 'info',
    });

    // @ts-expect-error — anonymousClient is registered in lib/better-auth-client.ts
    // but the plugin tuple's TS inference is erased by `plugins: any[]` there.
    // See better-auth-client.ts for the full rationale. Runtime behavior is correct.
    authClient.signIn.anonymous().then((result: { error: { message: string; status?: number } | null }) => {
      if (result.error) {
        Sentry.addBreadcrumb({
          category: 'auth.anon',
          message: 'Anonymous sign-in failed',
          level: 'warning',
          data: { error: result.error.message, status: result.error.status },
        });
        console.warn('[AuthContext] Anonymous sign-in failed:', result.error.message);
        // DRAMA-WEB-4A recovery: a 400 "Anonymous users cannot sign in again
        // anonymously" means the server already has a valid anon session for
        // our cookie — the imperative getSession() that informed our guard
        // returned null due to a transient race (most often iOS 13 Mobile
        // Safari SameSite=None cookie quirks). Re-running refreshSession()
        // picks up the existing session so the JWT-fetch effect can proceed
        // instead of stranding the user in a Bearerless 'anonymous' state.
        if (result.error.status === 400) {
          refreshSession().catch(() => {});
        }
        // Reset 'pending' (set by JWT-fetch effect during the bootstrap window)
        // back to 'anonymous' so authenticatedFetch awaitAuthReady doesn't hang.
        // DRAMA-EDGE-17 follow-up: prevents indefinite stalls when bootstrap fails.
        Sentry.addBreadcrumb({ category: 'auth.status', message: 'setAuthStatus: anonymous (anon bootstrap failed)', level: 'warning' });
        setAuthStatus('anonymous');
        return;
      }
      Sentry.addBreadcrumb({
        category: 'auth.anon',
        message: 'Anonymous sign-in succeeded',
        level: 'info',
      });
      // Funnel step 1 (NocoDB #3913): mark the start of the anon trial session.
      // Fires once per device install (anonSignInFiredRef guard).
      captureEvent('Auth_AnonSessionStarted', {});
      // Refresh the session so useImperativeSession picks up the new BA session.
      // The JWT-fetch effect is keyed on baSession?.session?.token and will
      // trigger automatically once refreshSession() updates baSession.
      refreshSession().catch(() => {
        // Best-effort: if refresh fails here the JWT-fetch effect will
        // not fire, but the user is still in a valid state (device-id path).
      });
    }).catch((err: unknown) => {
      console.warn('[AuthContext] Anonymous sign-in exception:', err);
      // Same recovery as the result.error branch — don't hang in 'pending'.
      Sentry.addBreadcrumb({ category: 'auth.status', message: 'setAuthStatus: anonymous (anon bootstrap exception)', level: 'warning' });
      setAuthStatus('anonymous');
    });
  }, [intentHydrated, sessionLoading, baSession, refreshSession, pathname]);

  // Reusable refresh callback factory for configureAuthToken.
  // Returns an async function that refreshes the JWT and re-registers itself.
  const getRefreshCallback = useCallback(() => async () => {
    jwtFetchedForTokenRef.current = null;
    const sessionToken = baSession?.session?.token;
    if (!sessionToken) return null;
    const newJwt = await fetchBetterAuthJwt(sessionToken);
    if (newJwt) {
      configureAuthToken(() => newJwt, getRefreshCallback());
      setJwt(newJwt);
    }
    return newJwt;
  }, [baSession?.session?.token]);

  // Fetch JWT when session token changes
  useEffect(() => {
    // Cold-load race (Sentry DRAMA-EDGE-17, NocoDB #4100 follow-up): while
    // useImperativeSession's BA session fetch is in flight, baSession is null
    // — but the session is NOT genuinely cleared. Setting status='anonymous'
    // here would resolve the pre-gate's bootstrap signal (PR #1158) and let
    // the request go out with no Bearer header. Instead, set 'pending' so
    // authenticatedFetch awaits via awaitAuthReady. The post-mount useEffect
    // re-runs once sessionLoading flips false + baSession resolves.
    if (sessionLoading) {
      Sentry.addBreadcrumb({ category: 'auth.status', message: 'setAuthStatus: pending (session loading)', level: 'info' });
      setAuthStatus('pending');
      return;
    }

    const sessionToken = baSession?.session?.token ?? null;
    if (!sessionToken) {
      // Pre-hydration window (DRAMA-EDGE-17 final⁴): if FF is ON but the
      // explicit-signout intent hasn't hydrated yet (AsyncStorage roundtrip
      // ~5-100ms), we cannot decide whether anon-bootstrap will fire. Setting
      // 'anonymous' now would resolve _pendingReady, drop the configured
      // token to null, and let the pre-gate ship a Bearerless request before
      // the next re-render flips intentHydrated. Hold as 'pending' instead
      // — the useEffect re-runs once intentHydrated flips (in the deps).
      if (FF_BA_ANONYMOUS_BOOTSTRAP && !intentHydrated) {
        Sentry.addBreadcrumb({ category: 'auth.status', message: 'setAuthStatus: pending (intent hydration in flight)', level: 'info' });
        setAuthStatus('pending');
        return;
      }

      // Anon-bootstrap window (DRAMA-EDGE-17 follow-up): when FF is ON and we
      // have no session yet, the anon-bootstrap useEffect below fires
      // signIn.anonymous() in parallel. That round-trip takes 1-3s. During
      // that window, setting 'anonymous' would resolve the pre-gate bootstrap
      // signal and let requests fire without a Bearer. Instead, gate as
      // 'pending' — the JWT-fetch effect re-runs once refreshSession() updates
      // baSession. The catch in the anon-bootstrap effect resets status to
      // 'anonymous' on failure so we don't hang in 'pending'.
      const willBootstrapAnon =
        FF_BA_ANONYMOUS_BOOTSTRAP &&
        !explicitSignedOutRef.current;
      if (willBootstrapAnon) {
        Sentry.addBreadcrumb({ category: 'auth.status', message: 'setAuthStatus: pending (anon bootstrap window)', level: 'info' });
        setAuthStatus('pending');
        return;
      }

      // Session genuinely cleared (post-signOut OR FF off / explicit signout)
      // — sync configureAuthToken BEFORE setJwt to prevent race
      Sentry.addBreadcrumb({ category: 'auth.status', message: 'setAuthStatus: anonymous (no session)', level: 'info' });
      configureAuthToken(() => null);
      setAuthStatus('anonymous');
      setJwt(null);
      setAuthBroken(false);
      jwtFetchedForTokenRef.current = null;
      return;
    }
    // Skip if we already fetched for this exact session token
    if (jwtFetchedForTokenRef.current === sessionToken) return;
    jwtFetchedForTokenRef.current = sessionToken;

    let cancelled = false;
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;

    const doFetch = () => {
      console.log('[AuthContext] doFetch: JWT fetch started, sessionToken prefix:', sessionToken.slice(0, 8) + '...');
      Sentry.addBreadcrumb({ category: 'auth.jwt', message: 'JWT fetch started', level: 'info', data: { sessionTokenPartial: sessionToken.slice(0, 8) + '...' } });
      // Register the refresh path before the JWT arrives so shared strict-call
      // recovery can still self-heal if a request races during bootstrap.
      configureAuthToken(() => null, getRefreshCallback());
      Sentry.addBreadcrumb({ category: 'auth.status', message: 'setAuthStatus: pending', level: 'info' });
      setAuthStatus('pending');
      fetchBetterAuthJwt(sessionToken).then((token) => {
        if (cancelled) {
          console.log('[AuthContext] doFetch: cancelled, ignoring JWT result');
          return;
        }
        if (!token) {
          console.log('[AuthContext] doFetch: JWT fetch FAILED — null token after retries, setting auth_broken');
          Sentry.addBreadcrumb({ category: 'auth.jwt', message: 'JWT fetch failed (null token after retries)', level: 'error' });
          // BA session is still present (we entered this branch because sessionToken existed)
          // but JWT mint failed after retries. Set 'auth_broken' so authenticatedFetch
          // refuses to silently downgrade requests to anonymous (NocoDB #3829).
          // The setOnAuthError callback wired in AuthContext will trigger signOut on the
          // next authenticatedFetch invocation, forcing the user to re-authenticate.
          Sentry.addBreadcrumb({ category: 'auth.status', message: 'setAuthStatus: auth_broken (JWT mint failed)', level: 'warning' });
          Sentry.captureMessage('Auth broken: JWT mint failed for active BA session', { level: 'warning' });
          jwtFetchedForTokenRef.current = null;
          setAuthStatus('auth_broken');
          setAuthBroken(true);
          setJwt(null);
          return;
        }
        console.log('[AuthContext] doFetch: JWT fetch SUCCESS');
        Sentry.addBreadcrumb({ category: 'auth.jwt', message: 'JWT fetch success', level: 'info' });
        // Sync configureAuthToken BEFORE setJwt to eliminate the 1-tick race.
        // React Query queryFns run during render; useEffect runs post-render.
        // By configuring the token provider here (in the async callback, before
        // triggering a re-render via setJwt), authenticatedFetch will already
        // have the token when React Query fires queries in the next render.
        Sentry.addBreadcrumb({ category: 'auth.jwt', message: 'configureAuthToken called', level: 'info' });
        configureAuthToken(() => token, getRefreshCallback());
        // Phase 2 (NocoDB #3844): keep status='anonymous' for anon BA sessions even
        // when a JWT is present. The JWT is still attached via the Bearer path in
        // authenticatedFetch (token > status), but `shouldNotifyAuthError` gates on
        // status==='authenticated' — so a 401 from a strict `auth` route will not
        // trigger the signOut cascade for anon users (who legitimately cannot access
        // those routes). Only real authenticated sessions enter the 'authenticated'
        // state and thus the silent-downgrade recovery path.
        const isAnon = !!baSession?.user?.isAnonymous;
        const nextStatus = isAnon ? 'anonymous' : 'authenticated';
        console.log(`[AuthContext] doFetch: setting auth status to '${nextStatus}' (isAnon=${isAnon})`);
        Sentry.addBreadcrumb({ category: 'auth.status', message: `setAuthStatus: ${nextStatus}`, level: 'info', data: { isAnonymous: isAnon } });
        setAuthStatus(nextStatus);
        setAuthBroken(false);
        setJwt(token);
        // Schedule refresh 1 minute before JWT expires (default 15 min)
        try {
          const payload = decodeJwtPayload(token);
          const expiresMs = ((payload?.exp as number) ?? 0) * 1000;
          const refreshIn = Math.max(expiresMs - Date.now() - 60_000, 30_000);
          refreshTimer = setTimeout(() => {
            if (!cancelled) {
              jwtFetchedForTokenRef.current = null; // allow re-fetch
              doFetch();
            }
          }, refreshIn);
        } catch { /* non-critical — JWT will expire and edge returns 401 */ }
      });
    };
    doFetch();

    return () => {
      cancelled = true;
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }, [baSession?.session?.token, sessionLoading, getRefreshCallback, intentHydrated]);

  // Fire pending anonymous data claim when JWT becomes available (#2896).
  // The user-id effect queues the deviceId here when prevUserIdRef was null;
  // we defer to this effect so the claim goes out with a valid bearer token.
  useEffect(() => {
    if (jwt && claimPendingDeviceIdRef.current) {
      const deviceId = claimPendingDeviceIdRef.current;
      claimPendingDeviceIdRef.current = null;
      authenticatedFetch(getEdgeFunctionUrl('claim-anonymous-data'), {
        method: 'POST',
        headers: { 'X-Device-Id': deviceId },
      }).catch(() => {});
    }
  }, [jwt]);

  // Ref for jwt so the visibility listener doesn't re-register on every JWT change (NocoDB #2884).
  const jwtRef = useRef<string | null>(null);
  useEffect(() => { jwtRef.current = jwt; }, [jwt]);

  // Layer 3 (NocoDB #2880): JWT refresh on tab focus (web only).
  // When the user returns to a backgrounded tab, the JWT may have expired.
  // Proactively refresh it so the next API call doesn't fail with 401.
  // Uses jwtRef instead of jwt to keep the listener stable across JWT rotations.
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const sessionToken = baSession?.session?.token;
      if (!sessionToken) return;

      const currentJwt = jwtRef.current;
      if (!currentJwt) return;

      const payload = decodeJwtPayload(currentJwt);
      const exp = (payload?.exp as number) ?? 0;
      const nowSec = Math.floor(Date.now() / 1000);
      // Refresh if JWT expires within 2 minutes
      if (exp < nowSec + 120) {
        Sentry.addBreadcrumb({ category: 'auth.jwt', message: 'Tab-focus JWT refresh triggered', level: 'info', data: { expIn: exp - nowSec } });
        jwtFetchedForTokenRef.current = null; // allow re-fetch
        fetchBetterAuthJwt(sessionToken).then((newToken) => {
          if (newToken) {
            // Sync configureAuthToken BEFORE setJwt (same race-fix pattern)
            Sentry.addBreadcrumb({ category: 'auth.jwt', message: 'Tab-focus JWT refresh success', level: 'info' });
            configureAuthToken(() => newToken, getRefreshCallback());
            // Phase 2 (NocoDB #3844 follow-up): mirror the isAnonymous guard from
            // the main doFetch path. Promoting anon users to 'authenticated' here
            // re-enables shouldNotifyAuthError on 401s from strict-auth routes —
            // the exact signOut cascade this PR closes.
            const isAnon = !!baSession?.user?.isAnonymous;
            setAuthStatus(isAnon ? 'anonymous' : 'authenticated');
            setJwt(newToken);
          }
        }).catch((err) => {
          console.warn('[AuthContext] Tab-focus JWT refresh failed:', err);
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [baSession?.session?.token, getRefreshCallback]);

  // BA-native session: expose JWT as accessToken and BA user fields directly.
  // IMPORTANT: Don't expose accessToken until JWT is available. Opaque session tokens
  // cause 401s on the edge functions during the ~1-2s window before JWT arrives.
  const accessToken = (baSession && jwt) ? jwt : null;
  const user: AuthUser | null = useMemo(
    () => baSession?.user ? {
      id: baSession.user.id,
      email: baSession.user.email,
      name: baSession.user.name,
      image: baSession.user.image ?? null,
      emailVerified: baSession.user.emailVerified,
      createdAt: new Date(baSession.user.createdAt).toISOString(),
      isAnonymous: !!baSession.user.isAnonymous,
    } : null,
    [baSession?.user?.id, baSession?.user?.email, baSession?.user?.name, baSession?.user?.image, baSession?.user?.emailVerified, baSession?.user?.createdAt, baSession?.user?.isAnonymous],
  );

  // Post-login side effects when user changes
  useEffect(() => {
    const userId = baSession?.user?.id ?? null;

    // Clear persisted query cache on REAL identity change (#2897).
    // Prevents cross-user data leakage when User A signs out and User B signs in.
    // IMPORTANT: Skip the initial null → userId transition on page reload —
    // clearing cache there wipes data that queries just fetched, causing
    // intermittent "No History Yet" / empty screens.
    if (
      userId !== prevUserIdRef.current &&
      prevUserIdRef.current !== null  // Only clear when switching between real users
    ) {
      queryClient.clear();
      AsyncStorage.removeItem(QUERY_CACHE_KEY).catch(() => {});
    }

    if (userId && userId !== prevUserIdRef.current && user) {
      // AUTH-UX-B (NocoDB #3907): if the previous identity was a real user_id
      // (not the initial null) and the new identity is a real authed user
      // (not anon), the BA `onLinkAccount` callback just re-keyed all the
      // anon-owned rows in Postgres. Tell PostHog the same: alias the new
      // distinctId to the old one so the funnel from anon-trial → signup
      // stays intact and the conversion event fires once per linkage.
      //
      // posthog-js (web) and posthog-react-native both expose `alias(alias, distinctId?)`.
      // Per the docs the first arg is the NEW id and the optional second is the
      // OLD id (defaults to current distinctId). We pass both explicitly to
      // avoid relying on PostHog's internal state at the exact transition tick.
      if (
        prevUserIdRef.current &&
        prevUserIdRef.current !== userId &&
        prevUserWasAnonRef.current &&
        !user.isAnonymous
      ) {
        try {
          const ph = getPostHog();
          // posthog.alias signature: alias(alias, distinctId?)
          // We want the new (authed) id aliased to the previous (anon) id.
          ph?.alias?.(userId, prevUserIdRef.current);
          captureEvent('Auth_AnonAccountLinked', {
            previous_user_id: prevUserIdRef.current,
            new_user_id: userId,
          });
        } catch {
          // Non-critical — funnel telemetry should never block sign-in.
        }
      }

      identifyUser(userId, {
        email: user.email,
        subscription_tier: 'free',
        created_at: user.createdAt,
      });
      try { getPostHog()?.reloadFeatureFlags?.(); } catch { /* non-critical */ }

      // Claim anonymous device data for newly authenticated user (#2896).
      // Transfers analysis_history rows from device_id to user_id.
      // Best-effort: failures don't block login. Deferred to configureDbClient
      // effect (keyed on jwt) so the request goes out with a valid bearer token —
      // jwt may still be null at this point due to an async network fetch.
      //
      // Phase 2 (NocoDB #3844 follow-up): skip for anon BA users. The
      // claim-anonymous-data endpoint is mounted as strict `auth` and rejects
      // role='anon' (correctly). An anon BA user claiming their own device
      // data is a no-op anyway — the device_id rows ARE their rows now. The
      // real multi-device merge flow is deferred to Phase 4.
      if (prevUserIdRef.current === null && !baSession?.user?.isAnonymous) {
        getDeviceId().then((deviceId) => {
          if (!deviceId) return;
          claimPendingDeviceIdRef.current = deviceId;
        }).catch(() => {});
      }

      // History prefetch lives in its own effect keyed on [jwt, userId] —
      // see below. Firing it here would race the JWT-fetch effect on first
      // page load and produce 401s (DRAMA-WEB-3C).
    }

    if (!userId && prevUserIdRef.current) {
      clearAnalyticsUser();
      prefetchDoneForUserRef.current = null;
    }

    prevUserWasAnonRef.current = !!user?.isAnonymous;
    prevUserIdRef.current = userId;
  }, [baSession?.user?.id, user]);

  // Prefetch history for instant History screen.
  //
  // Keyed on [jwt, userId] (NOT just userId) so the request goes out *after*
  // configureAuthToken() has wired up the bearer token. The prior behavior —
  // firing inside the user-id effect — raced the JWT-fetch effect on first
  // page load: BA session resolves synchronously from the cookie, but the
  // JWT effect runs on a later tick, so the prefetch went out token-less and
  // the worker returned 401. That produced DRAMA-WEB-3C ("History prefetch
  // non-auth error") on every E2E run.
  //
  // Pattern matches the device-claim effect above, which is keyed on [jwt]
  // for the same reason.
  //
  // Use fetchInfiniteQuery (not prefetchInfiniteQuery) so rejections propagate
  // to the .catch() handler. prefetchInfiniteQuery in TanStack Query v5 always
  // resolves internally via .then(noop).catch(noop), making any .catch() dead code.
  useEffect(() => {
    const userId = baSession?.user?.id;
    if (!jwt || !userId) return;
    if (prefetchDoneForUserRef.current === userId) return;
    prefetchDoneForUserRef.current = userId;

    queryClient.fetchInfiniteQuery({
      queryKey: queryKeys.analysis.history(userId, { pageSize: 15, category: undefined, infinite: true }),
      queryFn: async () => {
        const res = await getAnalysisHistory({ limit: 15, offset: 0 });
        if (res.error) {
          // Throw instead of returning [] — lets React Query retry on next mount
          // rather than caching an empty array as "success"
          throw res.error;
        }
        return res.data || [];
      },
      initialPageParam: 0,
      // CRITICAL: Must match useInfiniteAnalysisHistory's getNextPageParam exactly.
      // Without this, React Query caches the prefetched data without pagination
      // metadata, and the hook throws "getNextPageParam is not a function" on mount.
      getNextPageParam: (lastPage: unknown[]) => {
        return lastPage.length < 15 ? undefined : 15;
      },
    }).catch((err) => {
      // Best-effort — prefetch failures don't block login.
      // The useInfiniteAnalysisHistory hook will fetch fresh data when
      // the History tab mounts.
      //
      // Skip auth-required errors: by design, the JWT-keyed gate above
      // prevents the race that produced these. Any remaining auth-required
      // signal here means a refresh failed mid-flight — the AuthErrorBoundary
      // handles user-facing recovery.
      if (isAuthRequiredError(err)) return;
      // DRAMA-WEB-3B (NocoDB #4656): drop expected React Query cancellation +
      // transient network noise. Real 5xx + auth errors still flow to Sentry below.
      if (isCancelledError(err) || isTransientFetchError(err)) return;
      const errorCode = (err as { code?: string })?.code;
      const errorStatus = (err as { details?: { status?: number } })?.details?.status;
      const errorMessage = (err as { message?: string })?.message;
      // Pass `err` itself to Sentry's `extra` — it serializes objects natively.
      // The previous `String(err)` produced "[object Object]" because our
      // ApiResponse.error is a plain object, not an Error. DRAMA-WEB-3B fired
      // 477 times across 342 users with no usable diagnostic for 3 weeks
      // because of that gap.
      Sentry.captureMessage('History prefetch failed', {
        level: 'warning',
        extra: {
          userId,
          message: errorMessage,
          code: errorCode,
          status: errorStatus,
          rawError: err,
        },
      });
    });
  }, [jwt, baSession?.user?.id, queryClient]);

  // NocoDB #116 (2026-04-24): Defensive invalidation of analysis queries on JWT
  // transition (null → set). Prevents same-session empty history/stats for anon
  // users who:
  //   1. Boot the app with a BA session but no JWT yet (anon_pre_jwt state)
  //   2. Any transient query with their userId gets cached as error/empty
  //   3. JWT arrives, but the stale cache is served until `staleTime` (10m) elapses
  //
  // Invalidating on JWT arrival ensures every consumer of
  // `queryKeys.analysis.all(userId)` refetches once the bearer token is wired
  // into authenticatedFetch. Matches the consumer-side triple-gate
  // `enabled: !!user && !authLoading && !!accessToken` — this effect is the
  // producer-side equivalent.
  const prevJwtRef = useRef<string | null>(null);
  useEffect(() => {
    const userId = baSession?.user?.id;
    const hadJwt = !!prevJwtRef.current;
    const hasJwt = !!jwt;
    prevJwtRef.current = jwt;
    if (userId && !hadJwt && hasJwt) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.analysis.all(userId),
        exact: false,
      });
    }
  }, [jwt, baSession?.user?.id, queryClient]);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const result = await authClient.signIn.email({ email, password });
      if (result.error) {
        return { error: toAuthError(result.error.message) };
      }
      // Clear explicit sign-out intent so bootstrap is re-enabled for future
      // sessions (e.g. user signs out then signs back in, then signs out again).
      explicitSignedOutRef.current = false;
      AsyncStorage.removeItem(EXPLICIT_SIGNOUT_KEY).catch(() => {});
      await refreshSession();
      return { error: null };
    } catch (error) {
      return { error: toAuthError((error as Error).message) };
    }
  }, [refreshSession]);

  const signUp = useCallback(async (email: string, password: string) => {
    try {
      const result = await authClient.signUp.email({
        email,
        password,
        name: email.split('@')[0],
      });
      if (result.error) {
        return { error: toAuthError(result.error.message) };
      }
      explicitSignedOutRef.current = false;
      AsyncStorage.removeItem(EXPLICIT_SIGNOUT_KEY).catch(() => {});
      await refreshSession();
      return { error: null };
    } catch (error) {
      return { error: toAuthError((error as Error).message) };
    }
  }, [refreshSession]);

  const signOut = useCallback(async (options?: { explicit?: boolean }) => {
    // Only set the explicit sign-out intent for user-initiated sign-outs.
    // System-triggered sign-outs (auth error handler, JWT failures) pass
    // explicit: false to avoid blocking anonymous bootstrap for 30 days.
    const isExplicit = options?.explicit !== false;
    if (isExplicit) {
      // Set BEFORE any await so the bootstrap effect cannot race between
      // signOut() resolving and the next render cycle.
      explicitSignedOutRef.current = true;
      // Persist intent to AsyncStorage (fire-and-forget — ref already guards first render).
      AsyncStorage.setItem(EXPLICIT_SIGNOUT_KEY, JSON.stringify({ at: Date.now() })).catch(() => {});
    } else {
      // System sign-out (auth error, JWT failure): allow anonymous bootstrap to
      // re-fire so the user gets a fresh anon session rather than being stranded.
      anonSignInFiredRef.current = false;
    }

    try {
      const result = await authClient.signOut();
      if (result.error) {
        return { error: toAuthError(result.error.message) };
      }

      clearSession(); // Clear session state immediately on sign-out

      clearHistory();
      resetPreferences();
      queryClient.clear();

      try {
        await AsyncStorage.removeItem(QUERY_CACHE_KEY);
      } catch (storageError) {
        captureEvent('Logout_CacheClearError', { error: String(storageError) });
      }

      clearAnalyticsUser();
      return { error: null };
    } catch (error) {
      console.error('[AuthContext] SignOut exception:', error);
      return { error: toAuthError(error instanceof Error ? error.message : 'auth.signout_failed') };
    }
  }, [clearSession, clearHistory, resetPreferences, queryClient]);

  useEffect(() => {
    return () => {
      if (authErrorCooldownTimerRef.current) {
        clearTimeout(authErrorCooldownTimerRef.current);
      }
    };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    try {
      const callbackURL = Platform.OS === 'web'
        ? window.location.origin
        : Linking.createURL('auth/callback');
      // Clear explicit sign-out intent before initiating OAuth — the callback
      // will land on a protected route and we want bootstrap re-enabled if needed.
      explicitSignedOutRef.current = false;
      AsyncStorage.removeItem(EXPLICIT_SIGNOUT_KEY).catch(() => {});
      await authClient.signIn.social({ provider: 'google', callbackURL });
      captureEvent('GoogleSignin_Initiated', { provider: 'google', platform: Platform.OS });
    } catch (error) {
      console.error('[AuthContext] Google sign-in failed:', error);
      captureEvent('GoogleSignin_Failed', { error: scrubString(String(error)) });
      throw error;
    }
  }, []);

  // Wire authenticatedFetch's unrecoverable 401 handler to sign out (#2888).
  // When authenticatedFetch gets a 401 and JWT refresh fails, this fires.
  useEffect(() => {
    setOnAuthError(() => {
      const authStatus = getAuthStatus();
      if (authStatus === 'pending') {
        console.warn('[AuthContext] Unrecoverable auth error while pending — skipping signOut');
        captureEvent('Auth_UnrecoverableError_SkippedPending', { source: 'authenticatedFetch' });
        return;
      }

      if (authErrorSignOutLockRef.current) {
        console.warn('[AuthContext] Unrecoverable auth error throttled — signOut already in progress');
        captureEvent('Auth_UnrecoverableError_Debounced', { source: 'authenticatedFetch' });
        return;
      }

      authErrorSignOutLockRef.current = true;
      if (authErrorCooldownTimerRef.current) {
        clearTimeout(authErrorCooldownTimerRef.current);
      }
      authErrorCooldownTimerRef.current = setTimeout(() => {
        authErrorSignOutLockRef.current = false;
        authErrorCooldownTimerRef.current = null;
      }, 5000);

      console.warn('[AuthContext] Unrecoverable auth error — signing out');
      captureEvent('Auth_UnrecoverableError', { source: 'authenticatedFetch' });
      signOut({ explicit: false }).catch(() => {});
    });
    return () => setOnAuthError(null);
  }, [signOut]);

  // Exclude auth_broken from loading: when JWT mint fails, loading must be false
  // so downstream components render and trigger authenticatedFetch → onAuthError
  // → signOut self-heal. Without this exclusion, loading stays true indefinitely
  // because no fetches fire to trigger the recovery path (NocoDB #3830).
  const loading = sessionLoading || (!!baSession && !jwt && !authBroken);

  // Phase 2 (NocoDB #3869) — derived anonymity booleans.
  // These collapse ~15 local `!accessToken` / `!!accessToken && !user?.isAnonymous`
  // derivations across the app to a single authoritative source. Consumers use
  // `const { isAnonymous } = useAuth()` (or `isRealUser`) instead of re-deriving.
  const isAnonymous = !accessToken || !!user?.isAnonymous;
  const isRealUser = !!accessToken && !!user?.id && !user.isAnonymous;

  const value: AuthContextType = useMemo(() => ({
    accessToken,
    user,
    // Stay in loading state until JWT is fetched (prevents 401 burst from opaque tokens)
    loading,
    isAnonymous,
    isRealUser,
    signIn,
    signUp,
    signOut,
    signInWithGoogle,
  }), [accessToken, user, loading, isAnonymous, isRealUser, signIn, signUp, signOut, signInWithGoogle]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Hook to use auth context. Throws if used outside AuthProvider. */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
